/** Parsed city, region (state/province), and country from a comma-separated location string. */
export interface LocationParts {
  city: string;
  region: string;
  country: string;
}

/** Require at least three non-empty comma-separated parts: city, region, country. */
export function parseLocationParts(region: string): LocationParts | null {
  const parts = region.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  return {
    city: parts.slice(0, -2).join(', '),
    region: parts[parts.length - 2]!,
    country: parts[parts.length - 1]!,
  };
}

export function isValidGroundingRegion(region: string): boolean {
  return parseLocationParts(region) !== null;
}

function norm(s: string): string {
  return s.trim().toLowerCase();
}

const COUNTRY_DEFAULT_TZ: Record<string, string> = {
  'united states': 'America/New_York',
  usa: 'America/New_York',
  us: 'America/New_York',
  'u.s.': 'America/New_York',
  'u.s.a.': 'America/New_York',
  canada: 'America/Toronto',
  ca: 'America/Toronto',
  'united kingdom': 'Europe/London',
  uk: 'Europe/London',
  'great britain': 'Europe/London',
  england: 'Europe/London',
  scotland: 'Europe/London',
  wales: 'Europe/London',
  'northern ireland': 'Europe/London',
  australia: 'Australia/Sydney',
  france: 'Europe/Paris',
  germany: 'Europe/Berlin',
  italy: 'Europe/Rome',
  spain: 'Europe/Madrid',
  portugal: 'Europe/Lisbon',
  netherlands: 'Europe/Amsterdam',
  belgium: 'Europe/Brussels',
  switzerland: 'Europe/Zurich',
  austria: 'Europe/Vienna',
  sweden: 'Europe/Stockholm',
  norway: 'Europe/Oslo',
  denmark: 'Europe/Copenhagen',
  finland: 'Europe/Helsinki',
  ireland: 'Europe/Dublin',
  poland: 'Europe/Warsaw',
  'czech republic': 'Europe/Prague',
  czechia: 'Europe/Prague',
  greece: 'Europe/Athens',
  turkey: 'Europe/Istanbul',
  russia: 'Europe/Moscow',
  japan: 'Asia/Tokyo',
  china: 'Asia/Shanghai',
  'south korea': 'Asia/Seoul',
  korea: 'Asia/Seoul',
  india: 'Asia/Kolkata',
  singapore: 'Asia/Singapore',
  'hong kong': 'Asia/Hong_Kong',
  taiwan: 'Asia/Taipei',
  thailand: 'Asia/Bangkok',
  vietnam: 'Asia/Ho_Chi_Minh',
  indonesia: 'Asia/Jakarta',
  philippines: 'Asia/Manila',
  malaysia: 'Asia/Kuala_Lumpur',
  'new zealand': 'Pacific/Auckland',
  mexico: 'America/Mexico_City',
  brazil: 'America/Sao_Paulo',
  argentina: 'America/Argentina/Buenos_Aires',
  chile: 'America/Santiago',
  colombia: 'America/Bogota',
  peru: 'America/Lima',
  'south africa': 'Africa/Johannesburg',
  egypt: 'Africa/Cairo',
  nigeria: 'Africa/Lagos',
  kenya: 'Africa/Nairobi',
  israel: 'Asia/Jerusalem',
  'united arab emirates': 'Asia/Dubai',
  uae: 'Asia/Dubai',
  'saudi arabia': 'Asia/Riyadh',
};

const US_STATE_TZ: Record<string, string> = {
  alabama: 'America/Chicago',
  alaska: 'America/Anchorage',
  arizona: 'America/Phoenix',
  arkansas: 'America/Chicago',
  california: 'America/Los_Angeles',
  ca: 'America/Los_Angeles',
  colorado: 'America/Denver',
  connecticut: 'America/New_York',
  delaware: 'America/New_York',
  florida: 'America/New_York',
  fl: 'America/New_York',
  georgia: 'America/New_York',
  hawaii: 'Pacific/Honolulu',
  hi: 'Pacific/Honolulu',
  idaho: 'America/Boise',
  illinois: 'America/Chicago',
  indiana: 'America/Indiana/Indianapolis',
  iowa: 'America/Chicago',
  kansas: 'America/Chicago',
  kentucky: 'America/New_York',
  louisiana: 'America/Chicago',
  maine: 'America/New_York',
  maryland: 'America/New_York',
  massachusetts: 'America/New_York',
  michigan: 'America/Detroit',
  minnesota: 'America/Chicago',
  mississippi: 'America/Chicago',
  missouri: 'America/Chicago',
  montana: 'America/Denver',
  nebraska: 'America/Chicago',
  nevada: 'America/Los_Angeles',
  'new hampshire': 'America/New_York',
  'new jersey': 'America/New_York',
  'new mexico': 'America/Denver',
  'new york': 'America/New_York',
  ny: 'America/New_York',
  'north carolina': 'America/New_York',
  'north dakota': 'America/Chicago',
  ohio: 'America/New_York',
  oklahoma: 'America/Chicago',
  oregon: 'America/Los_Angeles',
  or: 'America/Los_Angeles',
  pennsylvania: 'America/New_York',
  'rhode island': 'America/New_York',
  'south carolina': 'America/New_York',
  'south dakota': 'America/Chicago',
  tennessee: 'America/Chicago',
  texas: 'America/Chicago',
  tx: 'America/Chicago',
  utah: 'America/Denver',
  vermont: 'America/New_York',
  virginia: 'America/New_York',
  washington: 'America/Los_Angeles',
  wa: 'America/Los_Angeles',
  'west virginia': 'America/New_York',
  wisconsin: 'America/Chicago',
  wyoming: 'America/Denver',
  'district of columbia': 'America/New_York',
  dc: 'America/New_York',
};

const CA_PROVINCE_TZ: Record<string, string> = {
  bc: 'America/Vancouver',
  'british columbia': 'America/Vancouver',
  ab: 'America/Edmonton',
  alberta: 'America/Edmonton',
  sk: 'America/Regina',
  saskatchewan: 'America/Regina',
  mb: 'America/Winnipeg',
  manitoba: 'America/Winnipeg',
  on: 'America/Toronto',
  ontario: 'America/Toronto',
  qc: 'America/Toronto',
  quebec: 'America/Toronto',
  québec: 'America/Toronto',
  nb: 'America/Moncton',
  'new brunswick': 'America/Moncton',
  ns: 'America/Halifax',
  'nova scotia': 'America/Halifax',
  pe: 'America/Halifax',
  'prince edward island': 'America/Halifax',
  nl: 'America/St_Johns',
  'newfoundland and labrador': 'America/St_Johns',
  yt: 'America/Whitehorse',
  yukon: 'America/Whitehorse',
  nt: 'America/Yellowknife',
  'northwest territories': 'America/Yellowknife',
  nu: 'America/Iqaluit',
  nunavut: 'America/Iqaluit',
};

const AU_STATE_TZ: Record<string, string> = {
  nsw: 'Australia/Sydney',
  'new south wales': 'Australia/Sydney',
  vic: 'Australia/Melbourne',
  victoria: 'Australia/Melbourne',
  qld: 'Australia/Brisbane',
  queensland: 'Australia/Brisbane',
  sa: 'Australia/Adelaide',
  'south australia': 'Australia/Adelaide',
  wa: 'Australia/Perth',
  'western australia': 'Australia/Perth',
  tas: 'Australia/Hobart',
  tasmania: 'Australia/Hobart',
  act: 'Australia/Sydney',
  nt: 'Australia/Darwin',
  'northern territory': 'Australia/Darwin',
};

function countryKey(country: string): string {
  return norm(country);
}

function regionKey(region: string): string {
  return norm(region);
}

function resolveByCountry(country: string): string | null {
  return COUNTRY_DEFAULT_TZ[countryKey(country)] ?? null;
}

function resolveRegionOverride(country: string, region: string): string | null {
  const c = countryKey(country);
  const r = regionKey(region);
  if (c === 'united states' || c === 'usa' || c === 'us' || c === 'u.s.' || c === 'u.s.a.') {
    return US_STATE_TZ[r] ?? null;
  }
  if (c === 'canada' || c === 'ca') {
    return CA_PROVINCE_TZ[r] ?? null;
  }
  if (c === 'australia') {
    return AU_STATE_TZ[r] ?? null;
  }
  return null;
}

/** Resolve an IANA timezone from a comma-separated city, region, country string. */
export function resolveLocationTimezone(locationRegion: string): string | null {
  const parts = parseLocationParts(locationRegion);
  if (!parts) return null;
  const override = resolveRegionOverride(parts.country, parts.region);
  if (override) return override;
  return resolveByCountry(parts.country);
}
